import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { WALLET_CURRENCY_MAX_LENGTH, WALLET_CURRENCY_MIN_LENGTH } from 'entities/wallet';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm, ModalFormProps } from 'shared/ui';
import { AddCurrencyModel } from '../../model';

export const createAddCurrencyModal = (model: AddCurrencyModel) => {
	const ModalForm = createModalForm(model);

	const CurrencyField = reflect({
		view: TextInput,
		bind: {
			placeholder: translated('add-currency.placeholder'),
			label: translated('add-currency.currency'),
			value: model.form.fields.code.$value,
			onChange: (e) => model.form.fields.code.change(e.target.value.toUpperCase()),
			'data-test-id': 'add-currency-input',
			errorProps: { 'data-test-id': 'add-currency-input-error' },
			error: combine(
				[model.form.fields.code.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-currency.currency'),
						minLength: WALLET_CURRENCY_MIN_LENGTH,
						maxLength: WALLET_CURRENCY_MAX_LENGTH,
					}),
			),
			required: true,
		},
	});

	const AddCurrencyModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending] = useUnit([model.$pending]);

		return (
			<ModalForm
				ref={ref}
				title={t('add-currency.title')}
				submitButtonProps={{
					loading: pending,
					children: t('add-currency.add'),
				}}
				cancelButtonProps={{
					children: t('add-currency.cancel'),
				}}
				{...props}
			>
				<CurrencyField />
			</ModalForm>
		);
	});

	const AddCurrencyModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-currency-button',
			variant: 'primary',
			leftSection: <Plus />,
			children: translated('add-currency.label'),
			onClick: model.switcher.enable,
			modalData: null,
			disabled: viewerModel.$role.map((role) => role !== USER_ROLES.god),
		},
	});

	return Object.assign(AddCurrencyModal, { Button: AddCurrencyModalButton });
};
